import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { FileText, Calendar, Search, Filter, RefreshCw, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function Dashboard() {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [casesRes, statsRes] = await Promise.all([
        axios.get(`${API}/cases`),
        axios.get(`${API}/stats`)
      ]);
      setCases(casesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (caseId) => {
    try {
      const res = await axios.post(`${API}/cases/${caseId}/refresh`);
      if (res.data.status === 'updated') {
        toast.success(`تم التحديث - ${res.data.changes} تغييرات`);
        fetchData();
      } else if (res.data.status === 'scraping_unavailable') {
        toast.warning('خدمة الجلب غير متوفرة حالياً. يمكنك التحديث يدوياً.');
      } else {
        toast.info('لا توجد تغييرات جديدة');
      }
    } catch (error) {
      toast.error('فشل التحديث');
    }
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = !searchQuery || 
      c.auto_number.includes(searchQuery) || 
      (c.case_number && c.case_number.includes(searchQuery));
    const matchesFilter = !filterStatus || c.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const statusColors = {
      'منظورة': 'bg-blue-100 text-blue-700',
      'مؤجلة': 'bg-yellow-100 text-yellow-700',
      'محجوزة للحكم': 'bg-purple-100 text-purple-700',
      'شطب': 'bg-gray-100 text-gray-700',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">لوحة التحكم</h1>
          <p className="text-gray-500 mt-1">متابعة جميع قضاياك في مكان واحد</p>
        </div>
        <Link to="/add-case">
          <Button data-testid="add-case-btn" className="gap-2 bg-blue-600 hover:bg-blue-700">
            <FileText className="w-4 h-4" />
            قضية جديدة
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="stat-total-cases" className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">إجمالي القضايا</p>
                <p className="text-3xl font-bold text-blue-900 mt-2">{stats.total_cases || 0}</p>
              </div>
              <FileText className="w-12 h-12 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-active-cases" className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">القضايا النشطة</p>
                <p className="text-3xl font-bold text-green-900 mt-2">{stats.active_cases || 0}</p>
              </div>
              <TrendingUp className="w-12 h-12 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-pending-cases" className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">القضايا المؤجلة</p>
                <p className="text-3xl font-bold text-yellow-900 mt-2">{stats.pending_cases || 0}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-notifications" className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">إشعارات جديدة</p>
                <p className="text-3xl font-bold text-purple-900 mt-2">{stats.unread_notifications || 0}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-purple-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>القضايا</CardTitle>
          <div className="flex gap-3 mt-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="البحث برقم القضية أو الرقم الآلي..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10"
              />
            </div>
            <select
              data-testid="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">جميع الحالات</option>
              <option value="منظورة">منظورة</option>
              <option value="مؤجلة">مؤجلة</option>
              <option value="محجوزة للحكم">محجوزة للحكم</option>
              <option value="شطب">شطب</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCases.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد قضايا</p>
              <Link to="/add-case">
                <Button className="mt-4" variant="outline">إضافة قضية جديدة</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCases.map((caseItem) => {
                // Get parties names for display
                const partiesDisplay = caseItem.parties && caseItem.parties.length > 0
                  ? caseItem.parties.map(p => p.name).filter(n => n).join(' ضد ')
                  : null;
                
                return (
                <div
                  key={caseItem.id}
                  data-testid={`case-item-${caseItem.id}`}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Link to={`/cases/${caseItem.id}`} className="hover:underline">
                        <h3 className="font-semibold text-lg text-gray-900">
                          {partiesDisplay || `قضية رقم ${caseItem.case_number || caseItem.auto_number}`}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span>الرقم الآلي: {caseItem.auto_number}</span>
                        {caseItem.case_number && <span>• رقم القضية: {caseItem.case_number}</span>}
                        {caseItem.court && <span>• {caseItem.court}</span>}
                      </div>
                      {caseItem.next_hearing && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
                          <Calendar className="w-4 h-4" />
                          <span>الجلسة القادمة: {new Date(caseItem.next_hearing.date).toLocaleDateString('ar-KW')}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusBadge(caseItem.status)}>
                        {caseItem.status || 'غير محدد'}
                      </Badge>
                      <Button
                        data-testid={`refresh-btn-${caseItem.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRefresh(caseItem.id)}
                        className="hover:bg-gray-100"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}